<script setup lang="ts">
import { computed, defineAsyncComponent, type Component } from 'vue';
import { useRoute } from 'vue-router';
import { getErrorComponentPath } from '@/modules/commissioning/utils/error-resolver';

// Pre-load all error page components using Vite's glob import
const errorPages = import.meta.glob('./**/*.vue', { eager: false });

const route = useRoute();

type DebugTroubleshootParams = {
  errorType: string;
  country: string;
  serialNumber: string;
  /** Optional segment from `troubleshoot/.../:failIds?` — comma-separated display ids (e.g. `1,2,3,4`). */
  failIds?: string | string[];
};

const serialNumber = computed(() => String((route.params as DebugTroubleshootParams).serialNumber ?? ''));
const countryCode = computed(() => String((route.params as DebugTroubleshootParams).country ?? ''));
const errorType = computed(() => String((route.params as DebugTroubleshootParams).errorType ?? ''));

/** Matches optional `:failIds?` on the debug troubleshoot route (single id or comma-separated). */
const failIdsFromRoute = computed((): string[] => {
  const p = (route.params as DebugTroubleshootParams).failIds;
  if (p == null || p === '') return [];
  const raw = Array.isArray(p) ? p.join(',') : String(p);
  return raw
    .split(',')
    .map((s) => decodeURIComponent('Com Error #' + s.trim()))
    .filter(Boolean);
});

const errorComponent = computed(() => {
  const sn = serialNumber.value;
  const et = errorType.value;
  const cc = countryCode.value;

  return defineAsyncComponent(() => {
    const componentPath = getErrorComponentPath(sn, et, cc);
    console.log(sn, et, cc, failIdsFromRoute.value, componentPath);
    const componentLoader = errorPages[componentPath];

    if (componentLoader) {
      return componentLoader() as Promise<{ default: Component }>;
    }

    // Fallback to default error screen
    return (
      (errorPages[getErrorComponentPath()]?.() as Promise<{ default: Component }>) ||
      Promise.resolve({ default: null })
    );
  });
});
</script>

<template>
  <component :is="errorComponent" :failIdsFromRoute="failIdsFromRoute" />
</template>
